/**
 * Lista contratti — la pagina che sblocca il censimento economico.
 *
 * Il dato reale, al 2026-09-04: importo annuo valorizzato su **4 contratti su 29**, giorni di
 * preavviso su **3**, importo massimo e url documento su **0**. Nessun backfill puo' riempirli,
 * perche' stanno sui PDF e nella testa dell'Ufficio Acquisti. La lista e' quindi costruita per
 * far vedere **cosa manca**, non solo cosa c'e': la colonna «da completare» esiste per questo.
 *
 * I filtri sono esattamente quelli che la route espone (`search`, `stato`, `fornitoreId`,
 * `tipologiaId`, `rsaId`, `disdettaEntroGiorni`): filtrare in memoria su una lista che il server
 * sa gia' filtrare significherebbe scaricare tutto e mentire sul conteggio. L'**ordinamento** per
 * scadenza invece e' in memoria: la lista non e' paginata ed e' di poche decine di righe.
 *
 * Dal 2026-10-30 (TASK-chiusura-contratti-pec): colonna «Disdetta» col quick link alla chiusura,
 * popup all'apertura con i contratti in scadenza nei prossimi 30 giorni, ordinamento per «Fine» e
 * «Disdetta entro». Il popup distingue **da disdire** (termine di disdetta entro 30 giorni) da **in
 * scadenza** (data di fine entro 30 giorni): un contratto a termine ha una fine ma niente da
 * disdire, e uno col termine passato ha una fine vicina e nessuna disdetta possibile.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@shared/context/AuthContext";
import Modal from "@shared/components/ui/Modal";
import {
  contrattiApi,
  fornitoriApi,
  anagraficheApi,
} from "../services/acquistiApi";
import {
  Campo,
  Scelta,
  Testo,
  Caricamento,
  Vuoto,
  Avviso,
} from "../components/campi";
import {
  STATO_CONTRATTO,
  STATO_CONTRATTO_BADGE,
  CONTRATTO_DISDETTO,
  CONTRATTO_DISMESSO,
  opzioni,
} from "../constants/optionSet";
import { dataIt, euro, giorniA, etichetta } from "../utils/formato";

/** Le finestre del job disdette: gli stessi tagli, cosi' la lista e le email dicono la stessa cosa. */
const FINESTRE_DISDETTA = [
  { value: 30, label: "entro 30 giorni" },
  { value: 60, label: "entro 60 giorni" },
  { value: 90, label: "entro 90 giorni" },
  { value: 120, label: "entro 120 giorni" },
];

/** Orizzonte del popup all'apertura. */
const GIORNI_POPUP = 30;
/** Il popup si mostra una volta per scheda del browser, non a ogni ritorno sulla lista. */
const CHIAVE_POPUP = "acquisti.contratti.popupScadenze.visto";

const chiuso = (c) =>
  [CONTRATTO_DISDETTO, CONTRATTO_DISMESSO].includes(Number(c.stato));

/**
 * Ordinamento per una colonna data, con le date vuote sempre in coda in entrambi i versi: una
 * data assente non e' «la piu' piccola», e' un dato mancante.
 */
function ordina(lista, ordine) {
  if (!ordine) return lista;
  const campo = ordine.campo;
  const segno = ordine.verso === "desc" ? -1 : 1;
  return [...lista].sort((a, b) => {
    const da = a[campo] ? String(a[campo]).slice(0, 10) : null;
    const db = b[campo] ? String(b[campo]).slice(0, 10) : null;
    if (da == null && db == null) return 0;
    if (da == null) return 1;
    if (db == null) return -1;
    return da < db ? -segno : da > db ? segno : 0;
  });
}

/** Intestazione cliccabile: ▲ crescente, ▼ decrescente, terzo clic torna all'ordine della route. */
function Ordinabile({ campo, ordine, setOrdine, children }) {
  const attivo = ordine?.campo === campo;
  const prossimo = () => {
    if (!attivo) return { campo, verso: "asc" };
    if (ordine.verso === "asc") return { campo, verso: "desc" };
    return null;
  };
  return (
    <th>
      <button
        type="button"
        className={`inline-flex items-center gap-1 hover:underline ${attivo ? "text-acquisti-700" : ""}`}
        onClick={() => setOrdine(prossimo())}
        title="Ordina per questa colonna"
      >
        {children}
        <span className="text-xs">
          {attivo ? (ordine.verso === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );
}

/** La colonna «Disdetta»: dove si e' nel percorso di chiusura, e il link per fare il passo dopo. */
function CellaDisdetta({ c, puoScrivere }) {
  if (Number(c.stato) === CONTRATTO_DISDETTO || c.dataDisdettaInviata) {
    return (
      <Link
        to={`/contratti/${c.id}?chiusura=1`}
        className="text-xs text-red-800 hover:underline"
        title={c.protocolloDisdetta || ""}
      >
        Inviata il {dataIt(c.dataDisdettaInviata)}
        {c.protocolloDisdetta && (
          <span className="block text-gray-500">{c.protocolloDisdetta}</span>
        )}
      </Link>
    );
  }
  if (Number(c.stato) === CONTRATTO_DISMESSO) {
    return <span className="text-gray-400">—</span>;
  }
  const g = giorniA(c.dataDisdettaEntro);
  if (g == null) return <span className="text-gray-400">—</span>;
  if (!puoScrivere) return <span className="text-gray-400">—</span>;
  if (g < 0) {
    return (
      <Link
        to={`/contratti/${c.id}?chiusura=D`}
        className="text-xs text-yellow-900 hover:underline"
        title="Il termine di disdetta è passato: proposta di risoluzione consensuale (modello D)"
      >
        Termine passato: risoluzione →
      </Link>
    );
  }
  return (
    <Link
      to={`/contratti/${c.id}?chiusura=1`}
      className="text-xs text-acquisti-700 font-medium hover:underline whitespace-nowrap"
    >
      Prepara disdetta →
    </Link>
  );
}

/** Il popup all'apertura: due gruppi, da disdire e in scadenza, nei prossimi 30 giorni. */
function PopupScadenze({ dati, onClose }) {
  const Gruppo = ({ titolo, righe, campoData, etichettaData, link }) =>
    righe.length === 0 ? null : (
      <div className="space-y-1">
        <h3 className="font-semibold text-gray-900 text-sm">{titolo}</h3>
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
          {righe.map((c) => {
            const g = giorniA(c[campoData]);
            return (
              <li
                key={c.id}
                className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/contratti/${c.id}`}
                    className="text-acquisti-700 hover:underline font-medium"
                    onClick={onClose}
                  >
                    {c.nome || "(senza nome)"}
                  </Link>
                  <span className="block text-xs text-gray-500">
                    {c.fornitore?.nome || "fornitore non indicato"}
                    {c.codice ? ` · ${c.codice}` : ""}
                  </span>
                </div>
                <span
                  className={g <= 7 ? "badge-danger" : "badge-warning"}
                  title={etichettaData}
                >
                  {etichettaData} {dataIt(c[campoData])} · {g} gg
                </span>
                {link && (
                  <Link
                    to={link(c)}
                    className="text-xs text-acquisti-700 hover:underline whitespace-nowrap"
                    onClick={onClose}
                  >
                    Prepara disdetta →
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Contratti in scadenza nei prossimi ${GIORNI_POPUP} giorni`}
      size="lg"
      footer={
        <button type="button" className="btn-primary" onClick={onClose}>
          Chiudi
        </button>
      }
    >
      <div className="space-y-4">
        <Gruppo
          titolo={`Da disdire entro ${GIORNI_POPUP} giorni`}
          righe={dati.daDisdire}
          campoData="dataDisdettaEntro"
          etichettaData="disdetta entro"
          link={(c) => `/contratti/${c.id}?chiusura=1`}
        />
        <Gruppo
          titolo={`In scadenza entro ${GIORNI_POPUP} giorni (fine contratto)`}
          righe={dati.inScadenza}
          campoData="dataFine"
          etichettaData="fine"
          link={null}
        />
        <p className="text-xs text-gray-500">
          Nel secondo gruppo stanno i contratti a termine e quelli il cui
          termine di disdetta è già passato: la scadenza è vicina ma non c'è più
          nulla da disdire. I contratti già disdetti o dismessi non compaiono.
          Questo avviso si mostra una volta per sessione.
        </p>
      </div>
    </Modal>
  );
}

/** Campi il cui vuoto blocca il lavoro a valle (gara, comparazione, notifica di disdetta). */
function mancanti(c) {
  const m = [];
  if (c.importoAnnuo == null) m.push("importo");
  if (c.giorniPreavviso == null) m.push("preavviso");
  if (!c.rsaCoperte?.length) m.push("RSA");
  return m;
}

function BadgeDisdetta({ data }) {
  const g = giorniA(data);
  if (g == null) return <span className="text-gray-400">—</span>;

  const classe =
    g < 0
      ? "badge-gray"
      : g <= 30
        ? "badge-danger"
        : g <= 90
          ? "badge-warning"
          : "badge-info";

  return (
    <span className={classe} title={dataIt(data)}>
      {g < 0 ? `scaduta da ${-g} gg` : `${g} gg`}
    </span>
  );
}

export default function ContrattiPage() {
  const { canPerformAction } = useAuth();
  const navigate = useNavigate();
  const puoScrivere = canPerformAction("contratti.gestisci");

  const [filtri, setFiltri] = useState({
    search: "",
    stato: "",
    fornitoreId: "",
    tipologiaId: "",
    rsaId: "",
    disdettaEntroGiorni: "",
  });
  const [contratti, setContratti] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState(null);
  const [ordine, setOrdine] = useState(null);
  const [popup, setPopup] = useState(null);

  const [fornitori, setFornitori] = useState([]);
  const [tipologie, setTipologie] = useState([]);
  const [rsa, setRsa] = useState([]);

  // Il popup delle scadenze: due chiamate a parte, indipendenti dai filtri, una volta per scheda.
  useEffect(() => {
    let visto = false;
    try {
      visto = sessionStorage.getItem(CHIAVE_POPUP) === "1";
    } catch {
      visto = false;
    }
    if (visto) return;
    let vivo = true;
    Promise.allSettled([
      contrattiApi.list({ disdettaEntroGiorni: GIORNI_POPUP }),
      contrattiApi.list({ fineEntroGiorni: GIORNI_POPUP }),
    ]).then(([d, f]) => {
      if (!vivo) return;
      const daDisdire = ordina(
        (d.status === "fulfilled" ? d.value.value || [] : []).filter(
          (c) => !chiuso(c),
        ),
        { campo: "dataDisdettaEntro", verso: "asc" },
      );
      const giaDentro = new Set(daDisdire.map((c) => c.id));
      const inScadenza = ordina(
        (f.status === "fulfilled" ? f.value.value || [] : []).filter(
          (c) => !chiuso(c) && !giaDentro.has(c.id),
        ),
        { campo: "dataFine", verso: "asc" },
      );
      if (daDisdire.length + inScadenza.length > 0) {
        setPopup({ daDisdire, inScadenza });
      }
      try {
        sessionStorage.setItem(CHIAVE_POPUP, "1");
      } catch {
        /* storage non disponibile: il popup tornera' alla prossima apertura */
      }
    });
    return () => {
      vivo = false;
    };
  }, []);

  // Le anagrafiche riempiono i menu dei filtri: si caricano una volta sola.
  useEffect(() => {
    let vivo = true;
    Promise.allSettled([
      fornitoriApi.list(),
      anagraficheApi.tipologieFornitura(),
      anagraficheApi.rsa(),
    ]).then(([f, t, r]) => {
      if (!vivo) return;
      if (f.status === "fulfilled") setFornitori(f.value.value || []);
      if (t.status === "fulfilled") setTipologie(t.value.value || []);
      if (r.status === "fulfilled") setRsa(r.value.value || []);
    });
    return () => {
      vivo = false;
    };
  }, []);

  const carica = useCallback(async () => {
    setCaricamento(true);
    setErrore(null);
    try {
      const res = await contrattiApi.list(filtri);
      setContratti(res.value || []);
    } catch (e) {
      setErrore(e.message || "Impossibile caricare i contratti");
    } finally {
      setCaricamento(false);
    }
  }, [filtri]);

  // Ricarica a ogni cambio di filtro, con una pausa breve: la `search` si scrive a mano e una
  // chiamata per tasto premuto e' inutile su una lista di questa dimensione.
  useEffect(() => {
    const t = setTimeout(carica, 300);
    return () => clearTimeout(t);
  }, [carica]);

  const set = (chiave) => (valore) =>
    setFiltri((f) => ({ ...f, [chiave]: valore }));

  const riepilogo = useMemo(() => {
    const senzaImporto = contratti.filter((c) => c.importoAnnuo == null).length;
    const senzaPreavviso = contratti.filter(
      (c) => c.giorniPreavviso == null,
    ).length;
    const totale = contratti.reduce(
      (s, c) => s + (Number(c.importoAnnuo) || 0),
      0,
    );
    return { senzaImporto, senzaPreavviso, totale };
  }, [contratti]);

  const ordinati = useMemo(
    () => ordina(contratti, ordine),
    [contratti, ordine],
  );

  return (
    <div className="space-y-4">
      {popup && <PopupScadenze dati={popup} onClose={() => setPopup(null)} />}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900">Contratti</h1>
        <span className="text-sm text-gray-500">
          {caricamento ? "…" : `${contratti.length} risultati`}
        </span>
        <div className="ml-auto">
          {puoScrivere && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate("/contratti/nuovo")}
            >
              + Nuovo contratto
            </button>
          )}
        </div>
      </div>

      <div className="card !p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <Campo label="Cerca">
            <Testo
              valore={filtri.search}
              onChange={set("search")}
              placeholder="nome o codice"
            />
          </Campo>
          <Campo label="Stato">
            <Scelta
              valore={filtri.stato}
              onChange={set("stato")}
              opzioni={opzioni(STATO_CONTRATTO)}
              vuoto="tutti"
            />
          </Campo>
          <Campo label="Fornitore">
            <Scelta
              valore={filtri.fornitoreId}
              onChange={set("fornitoreId")}
              opzioni={fornitori.map((f) => ({ value: f.id, label: f.nome }))}
              vuoto="tutti"
            />
          </Campo>
          <Campo label="Tipologia">
            <Scelta
              valore={filtri.tipologiaId}
              onChange={set("tipologiaId")}
              opzioni={tipologie.map((t) => ({ value: t.id, label: t.nome }))}
              vuoto="tutte"
            />
          </Campo>
          <Campo label="RSA coperta">
            <Scelta
              valore={filtri.rsaId}
              onChange={set("rsaId")}
              opzioni={rsa.map((r) => ({ value: r.id, label: r.nome }))}
              vuoto="tutte"
            />
          </Campo>
          <Campo label="Disdetta">
            <Scelta
              valore={filtri.disdettaEntroGiorni}
              onChange={set("disdettaEntroGiorni")}
              opzioni={FINESTRE_DISDETTA}
              vuoto="qualsiasi"
            />
          </Campo>
        </div>
      </div>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {!caricamento && contratti.length > 0 && (
        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
          <span>
            Totale importi noti: <strong>{euro(riepilogo.totale)}</strong>
          </span>
          {riepilogo.senzaImporto > 0 && (
            <span className="text-yellow-800">
              {riepilogo.senzaImporto} senza importo annuo
            </span>
          )}
          {riepilogo.senzaPreavviso > 0 && (
            <span className="text-yellow-800">
              {riepilogo.senzaPreavviso} senza preavviso — nessuna notifica di
              disdetta partirà per questi
            </span>
          )}
        </div>
      )}

      <div className="card !p-0 overflow-x-auto">
        {caricamento ? (
          <Caricamento />
        ) : contratti.length === 0 ? (
          <Vuoto titolo="Nessun contratto">
            Nessun contratto corrisponde ai filtri impostati.
          </Vuoto>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Contratto</th>
                <th>Fornitore</th>
                <th>Tipologia</th>
                <th>RSA</th>
                <th>Stato</th>
                <Ordinabile
                  campo="dataFine"
                  ordine={ordine}
                  setOrdine={setOrdine}
                >
                  Fine
                </Ordinabile>
                <Ordinabile
                  campo="dataDisdettaEntro"
                  ordine={ordine}
                  setOrdine={setOrdine}
                >
                  Disdetta entro
                </Ordinabile>
                <th>Disdetta</th>
                <th className="text-right">Importo annuo</th>
                <th>Da completare</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ordinati.map((c) => {
                const m = mancanti(c);
                return (
                  <tr key={c.id}>
                    <td>
                      <Link
                        to={`/contratti/${c.id}`}
                        className="text-acquisti-700 hover:underline font-medium"
                      >
                        {c.nome || "(senza nome)"}
                      </Link>
                      {c.codice && (
                        <span className="block text-xs text-gray-500">
                          {c.codice}
                        </span>
                      )}
                    </td>
                    <td>{c.fornitore?.nome || "—"}</td>
                    <td
                      title={(c.tipologieCoperte || [])
                        .map((t) => t.nome)
                        .join(", ")}
                    >
                      {c.tipologiaFornitura?.nome || "—"}
                      {c.tipologieCoperte?.length > 1 && (
                        <span className="block text-xs text-gray-500">
                          + altri {c.tipologieCoperte.length - 1} servizi
                        </span>
                      )}
                    </td>
                    <td>
                      {c.rsaCoperte?.length
                        ? c.rsaCoperte.length === 1
                          ? c.rsaCoperte[0].nome
                          : `${c.rsaCoperte.length} RSA`
                        : "—"}
                    </td>
                    <td>
                      <span
                        className={
                          STATO_CONTRATTO_BADGE[c.stato] || "badge-gray"
                        }
                      >
                        {etichetta(STATO_CONTRATTO, c.stato)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap">{dataIt(c.dataFine)}</td>
                    <td className="whitespace-nowrap">
                      <BadgeDisdetta data={c.dataDisdettaEntro} />
                    </td>
                    <td>
                      <CellaDisdetta c={c} puoScrivere={puoScrivere} />
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {euro(c.importoAnnuo)}
                    </td>
                    <td>
                      {m.length === 0 ? (
                        <span className="text-green-700 text-xs">completo</span>
                      ) : (
                        <span className="text-xs text-yellow-800">
                          {m.join(", ")}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
